import { useCallback, useRef, useState } from 'react';
import { getInvoiceGroups } from '@rideiq/organizers/api/services/Billing';
import { InvoiceGroup } from '@rideiq/organizers/features/InvoiceGroups/types';

interface IUseInvoiceGroups {
  invoiceGroups: InvoiceGroup[];
  totalCount: number;
  isFetching: boolean;
  hasFetched: boolean;
  fetchInvoiceGroups: (params: QueryParams, isPaginated?: boolean) => void;
  addMissingInvoiceGroup: (group: InvoiceGroup) => void;
}

export const useInvoiceGroups = (): IUseInvoiceGroups => {
  const [invoiceGroups, setInvoiceGroups] = useState<InvoiceGroup[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [isFetching, setIsFetching] = useState(false);
  const [hasFetched, setHasFetched] = useState(false);
  const abortController = useRef<AbortController>();

  const fetchInvoiceGroups = useCallback(async (params: QueryParams, isPaginated?: boolean) => {
    abortController.current = new AbortController();
    setIsFetching(true);
    const response = await getInvoiceGroups(params, abortController.current.signal);

    if (response.type === 'success') {
      if (params.page === 1 || isPaginated) {
        setInvoiceGroups(response.data.invoiceGroups);
      } else {
        setInvoiceGroups((prev) => [...prev, ...response.data.invoiceGroups]);
      }
      setTotalCount(response.data.meta.totalCount);
      setHasFetched(true);
    }

    setIsFetching(false);
  }, []);

  const addMissingInvoiceGroup = async (group) => {
    if (invoiceGroups.find((invoiceGroup) => invoiceGroup.id === group.id)) {
      return;
    }
    setInvoiceGroups([...invoiceGroups, group]);
  };

  return {
    invoiceGroups,
    totalCount,
    isFetching,
    hasFetched,
    fetchInvoiceGroups,
    addMissingInvoiceGroup,
  };
};
